import type { PlotRequest, RecordValue } from "@tento-data";
import { PlotCollection } from "@tento-data";

import type { CanvasRenderTarget } from "../canvas/canvas-types";
import type { CanvasSeriesRenderer } from "./renderer";
import { toCanvasWidth, toCanvasX } from "./scale-utils";

export type FunnelRendererStyle = Readonly<{
  fillStyle: string;
  stages: readonly string[];
  minBarPx: number;
  ghostAlpha: number;
}>;

/**
 * Canvas funnel renderer for record payloads: one per-day glyph of VERTICAL
 * bars sitting side-by-side within the day column, one bar per conversion
 * stage (clicks → … → pay). Each glyph is SELF-NORMALIZED — bar heights scale
 * to the largest stage in that record, so the shape reads as a drop-off funnel
 * regardless of the canvas y-scale (record entries contribute no yExtent, and
 * this renderer ignores `request.yExtent` by design). Behind each real bar a
 * faint "ghost" of the previous stage's height is drawn first (same sub-column,
 * full stage width), so the height lost at each step (the conversion cliff)
 * reads at a glance.
 */
export class FunnelRenderer implements CanvasSeriesRenderer {
  constructor(private readonly style: FunnelRendererStyle = {
    fillStyle: "#999cff",
    stages: [],
    minBarPx: 2,
    ghostAlpha: 0.18,
  }) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return PlotCollection.isRecordEntry(entry);
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    if (!PlotCollection.isRecordEntry(entry)) return;

    const { width, height } = target.size;
    // One day in domain units → the glyph's horizontal span, padded in.
    const slotW = clamp(
      Math.abs(toCanvasWidth(1, request.viewRange, width)) * 0.85,
      8,
      160,
    );
    // Bars grow up from the bottom padding; heights map into the padded band.
    const availH = height - 8;
    const baseline = height - 4;

    target.ctx.fillStyle = this.style.fillStyle;

    for (const [x, record] of entry.series.range(
      request.viewRange.start,
      request.viewRange.end,
    ).points()) {
      // Explicit stage order when configured; otherwise the record's own keys
      // in insertion order.
      const stages = this.style.stages.length > 0
        ? this.style.stages
        : Object.keys(record);
      const values = stages.map((field) => fieldNumber(record, field));

      // Self-normalize to the biggest stage in THIS record.
      let maxVal = 0;
      for (const value of values) maxVal = Math.max(maxVal, value);
      if (maxVal <= 0) continue;

      const n = stages.length;
      const x0 = toCanvasX(x, request.viewRange, width);
      // Each stage owns a sub-column of the slot, left → right.
      const stageW = slotW / n;

      for (let i = 0; i < n; i++) {
        const value = values[i];
        if (value <= 0) continue;

        const barX = x0 + i * stageW;
        const barH = Math.max(this.style.minBarPx, (value / maxVal) * availH);

        // Ghost of the previous stage's height — the drop-off cliff — drawn
        // in the same sub-column, at full stage width, behind the real bar.
        if (i > 0 && values[i - 1] > 0) {
          const ghostH = Math.max(
            this.style.minBarPx,
            (values[i - 1] / maxVal) * availH,
          );
          target.ctx.save();
          target.ctx.globalAlpha = this.style.ghostAlpha;
          target.ctx.fillRect(barX, baseline - ghostH, stageW, ghostH);
          target.ctx.restore();
        }

        target.ctx.fillRect(barX, baseline - barH, stageW, barH);
      }
    }
  }
}

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

function fieldNumber(record: RecordValue, field: string): number {
  const value = record[field];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}
